import { useMergeLink } from "@mergeapi/react-merge-link";

type AdditionalSuccessfulLinkInfo = {
  isTokenForOtherPreexistingLinkedAccount?: boolean;
};
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import { gql } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { GraphqlOperations } from "@src/types.generated";
import {
  useGenerateAccountTokenMutation,
  useGenerateLinkTokenMutation,
} from "./useOpenMergeLink.generated";

export const useOpenMergeLink = (
  initialIntegrationId?: string,
): (() => void) => {
  const [linkToken, setLinkToken] = useState("");
  const [newIntegrationId, setNewIntegrationId] = useState("");
  const isRelink = Boolean(initialIntegrationId);
  const [generateLinkToken] = useGenerateLinkTokenMutation({
    onCompleted: (data) => {
      if (
        data.generateMergeLinkToken.value &&
        data.generateMergeLinkToken.hrisIntegrationId
      ) {
        setLinkToken(data.generateMergeLinkToken.value);
        setNewIntegrationId(data.generateMergeLinkToken.hrisIntegrationId);
      }
    },
    onError: (err) => {
      datadogRum.addError(err, {
        source: "merge_link_token_generation",
        integrationId: initialIntegrationId,
      });
      toast.error("Failed to open HRIS connection dialog");
    },
  });

  const { addToast } = useToast();
  const { trackEvent } = useTracking();
  const [generateAccountToken] = useGenerateAccountTokenMutation({
    onError: (err) => {
      datadogRum.addError(err, {
        source: "merge_account_token_generation",
        integrationId: initialIntegrationId,
      });
      toast.error("Failed to complete HRIS connection");
    },
  });
  const onSuccess = useCallback(
    (publicToken: string, additionalInfo?: AdditionalSuccessfulLinkInfo) => {
      if (!newIntegrationId) {
        return;
      }
      trackEvent("connected_hris", {});
      addToast({
        message:
          "Your HRIS has been connected! It can take several minutes to pull in your data.",
      });
      if (additionalInfo?.isTokenForOtherPreexistingLinkedAccount || isRelink) {
        return;
      }
      generateAccountToken({
        refetchQueries: [
          GraphqlOperations.Query.OrgHrisConfig,
          GraphqlOperations.Query.HrisIntegrations,
        ],
        variables: {
          publicToken,
          hrisIntegrationId: newIntegrationId,
        },
      });
    },
    [generateAccountToken, trackEvent, addToast, newIntegrationId, isRelink],
  );

  const onExit = useCallback(() => {
    setLinkToken("");
  }, [setLinkToken]);

  const { open, isReady, error } = useMergeLink({
    linkToken,
    onSuccess,
    onExit,
    shouldSendTokenOnSuccessfulLink: true,
  } as Parameters<typeof useMergeLink>[0]);
  const canOpenLink = isReady && linkToken;

  useEffect(() => {
    if (error) {
      datadogRum.addError(error, {
        source: "merge_link",
        integrationId: initialIntegrationId,
      });
      toast.error("Something went wrong with the HRIS connection dialog");
    }
  }, [error, initialIntegrationId]);

  useEffect(() => {
    if (canOpenLink) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOpenLink]);

  return useCallback(() => {
    if (canOpenLink) {
      open();
    } else {
      generateLinkToken({
        variables: {
          integrationId: initialIntegrationId,
        },
      });
    }
  }, [canOpenLink, initialIntegrationId, open, generateLinkToken]);
};

gql`
  mutation GenerateAccountToken(
    $publicToken: String!
    $hrisIntegrationId: String!
  ) {
    generateMergeAccountToken(
      publicToken: $publicToken
      hrisIntegrationId: $hrisIntegrationId
    ) {
      success
    }
  }
  mutation GenerateLinkToken($integrationId: UUID) {
    generateMergeLinkToken(integrationId: $integrationId) {
      value
      hrisIntegrationId
    }
  }
`;
